// Blocos de endereço reutilizados por (quase) todos os pedidos de Seleção e Inscrição.
// Cada config decide apenas se precisa do passo completo (residencial + profissional +
// correspondência) ou só do residencial, via enderecoStep({ comProfissional }).

// `autoCep: true` (piloto, 2026-08) liga a busca de CEP na API de Endereços: o
// campo CEP passa a autopreencher Logradouro/Bairro/Município/UF/País no blur, e
// esses 5 campos ficam `disabled` (só são preenchidos por essa busca — nunca
// digitados à mão, pra nunca divergir do que a API devolveu). Se o CEP não for
// encontrado, os campos ficam vazios e a validação de obrigatório já barra o
// "Próximo" sozinha — ver fieldComponents.js (tipo `cep`) pro detalhe da busca.
// `comEnderecoEmSeuNome = false` tira a pergunta "O endereço informado está em
// seu nome?" — só faz sentido nos pedidos que têm algum documento condicional
// dependendo dela (ex: comprovante de residência de terceiros, via `dependsOn:
// {field: 'enderecoEmSeuNome'}`); pedidos sem documento nenhum (ex: Certidões)
// não precisam da pergunta.
const enderecoResidencialSection = ({ autoCep = false, comEnderecoEmSeuNome = true } = {}) => ({
  title: 'Endereço Residencial (Obrigatório)',
  fields: [
    {
      type: 'cep', name: 'cepResidencial', label: 'CEP', required: true,
      ...(autoCep && {
        enderecoFields: {
          logradouro: 'logradouroResidencial', bairro: 'bairroResidencial',
          municipio: 'municipioResidencial', estado: 'ufResidencial', pais: 'paisResidencial'
        }
      })
    },
    { type: 'text', name: 'logradouroResidencial', label: 'Logradouro', required: true, disabled: autoCep },
    { type: 'text', name: 'numeroResidencial', label: 'Número', required: true },
    { type: 'text', name: 'complementoResidencial', label: 'Complemento' },
    { type: 'text', name: 'bairroResidencial', label: 'Bairro', required: true, disabled: autoCep },
    { type: 'text', name: 'municipioResidencial', label: 'Município', required: true, disabled: autoCep },
    { type: 'text', name: 'ufResidencial', label: 'UF', required: true, disabled: autoCep },
    { type: 'text', name: 'paisResidencial', label: 'País', defaultValue: 'Brasil', required: true, disabled: autoCep },
    { type: 'phone', name: 'telefoneResidencial', label: 'Telefone Fixo' },
    { type: 'phone', name: 'celularResidencial', label: 'Celular', required: true },
    { type: 'email', name: 'emailResidencial', label: 'E-mail', required: true, placeholder: 'email@exemplo.com' },
    ...(comEnderecoEmSeuNome ? [{
      type: 'radio',
      name: 'enderecoEmSeuNome',
      label: 'O endereço informado está em seu nome?',
      required: true,
      options: [{ value: 'SIM', label: 'Sim' }, { value: 'NAO', label: 'Não' }],
      defaultValue: 'SIM'
    }] : [])
  ]
});

// Campos do endereço profissional/comercial (sufixo "Profissional", que a API
// chama de "Comercial"). `showWhen` é repassado a todos: no bloco opcional da PF
// eles só aparecem depois do "Sim"; no endereço comercial de PJ, sempre.
const camposEnderecoProfissional = ({ autoCep = false, showWhen } = {}) => [
  {
    type: 'cep', name: 'cepProfissional', label: 'CEP', required: true,
    ...(autoCep && {
      enderecoFields: {
        logradouro: 'logradouroProfissional', bairro: 'bairroProfissional',
        municipio: 'municipioProfissional', estado: 'ufProfissional'
      }
    })
  },
  { type: 'text', name: 'logradouroProfissional', label: 'Logradouro', required: true, disabled: autoCep },
  { type: 'text', name: 'numeroProfissional', label: 'Número', required: true },
  { type: 'text', name: 'complementoProfissional', label: 'Complemento' },
  { type: 'text', name: 'bairroProfissional', label: 'Bairro', required: true, disabled: autoCep },
  { type: 'text', name: 'municipioProfissional', label: 'Município', required: true, disabled: autoCep },
  { type: 'text', name: 'ufProfissional', label: 'UF', required: true, disabled: autoCep },
  { type: 'phone', name: 'telefoneProfissional', label: 'Telefone' },
  { type: 'phone', name: 'celularProfissional', label: 'Celular', required: true },
  { type: 'email', name: 'emailProfissional', label: 'E-mail', required: true, placeholder: 'email@exemplo.com' }
].map(field => (showWhen ? { ...field, showWhen } : field));

const enderecoProfissionalSection = ({ autoCep = false } = {}) => ({
  title: 'Endereço Profissional (Opcional)',
  fields: [
    {
      type: 'radio',
      name: 'temEnderecoProfissional',
      label: 'Possui endereço profissional?',
      required: true,
      options: [{ value: 'SIM', label: 'Sim' }, { value: 'NAO', label: 'Não' }],
      defaultValue: 'NAO'
    },
    ...camposEnderecoProfissional({ autoCep, showWhen: { field: 'temEnderecoProfissional', value: 'SIM' } })
  ]
});

const enderecoCorrespondenciaSection = () => ({
  title: 'Endereço para Correspondência',
  fields: [
    {
      type: 'radio',
      name: 'enderecoCorrespondencia',
      label: 'Escolha o endereço para receber as correspondências',
      required: true,
      options: [
        { value: 'RESIDENCIAL', label: 'Endereço Residencial' }
      ],
      defaultValue: 'RESIDENCIAL',
      showWhen: { field: 'temEnderecoProfissional', value: 'NAO' }
    },
    {
      type: 'radio',
      name: 'enderecoCorrespondencia',
      label: 'Escolha o endereço para receber as correspondências',
      required: true,
      options: [
        { value: 'RESIDENCIAL', label: 'Endereço Residencial' },
        { value: 'PROFISSIONAL', label: 'Endereço Profissional' }
      ],
      defaultValue: 'RESIDENCIAL',
      showWhen: { field: 'temEnderecoProfissional', value: 'SIM' }
    }
  ]
});

/**
 * Passo de endereço completo, pronto para entrar em `config.steps`.
 * comProfissional: true -> Residencial + Profissional + Correspondência (id/title no plural)
 * comProfissional: false -> só Residencial (id/title no singular)
 * autoCep: true -> liga a busca de CEP na API de Endereços (ver enderecoResidencialSection)
 * comEnderecoEmSeuNome: false -> tira a pergunta "O endereço informado está em seu nome?" (ver enderecoResidencialSection)
 */
export const enderecoStep = ({ comProfissional = false, autoCep = false, comEnderecoEmSeuNome = true, id, title } = {}) => ({
  id: id || (comProfissional ? 'enderecos' : 'endereco'),
  title: title || (comProfissional ? 'Endereços' : 'Endereço'),
  sections: comProfissional
    ? [enderecoResidencialSection({ autoCep, comEnderecoEmSeuNome }), enderecoProfissionalSection({ autoCep }), enderecoCorrespondenciaSection()]
    : [enderecoResidencialSection({ autoCep, comEnderecoEmSeuNome })]
});

/**
 * Passo de endereço de pessoa jurídica: só o Endereço Comercial, obrigatório —
 * sem residencial, sem "está em seu nome?", sem correspondência. Usa os mesmos
 * campos "...Profissional" do bloco opcional da PF, então pré-preenchimento,
 * PUT /pessoa/:id (colunas "Comercial") e DADOS_REQUERIMENTO_ENDERECO_PROFISSIONAL
 * funcionam sem nada a mais. Usado como `versaoPJ` do enderecoStep da Atualização
 * Cadastral (ver adaptarConfigAoPerfil em acessoPedidoService) e direto nos 2 pedidos
 * de Associação entre Sociedades (com `tituloConfig.enderecoOrigem: 'comercial'`).
 */
export const enderecoComercialStep = ({ autoCep = false, id = 'enderecos', title = 'Endereço' } = {}) => ({
  id,
  title,
  sections: [{ title: 'Endereço Comercial', fields: camposEnderecoProfissional({ autoCep }) }]
});

// Fragmentos de dadosRequerimentoConfig (ver dadosRequerimentoService.js) pro
// endereço deste dialeto (cep/logradouro/.../{Residencial,Profissional}) —
// confirmados ao vivo contra GET /modelo-requerimento/:id/campos: o sufixo local
// "Profissional" corresponde ao sufixo "Comercial" da API (não "Profissional").
// Um pedido com enderecoStep() usa só RESIDENCIAL; com
// enderecoStep({comProfissional:true}) usa os dois, via mergeDadosRequerimentoConfig.
export const DADOS_REQUERIMENTO_ENDERECO_RESIDENCIAL = {
  campos: [
    { field: 'logradouroResidencial', campoNome: 'LogradouroResidencial' },
    { field: 'numeroResidencial', campoNome: 'NumeroResidencial' },
    { field: 'complementoResidencial', campoNome: 'ComplementoResidencial' },
    { field: 'bairroResidencial', campoNome: 'BairroResidencial' },
    { field: 'telefoneResidencial', campoNome: 'TelefoneResidencial' },
    { field: 'celularResidencial', campoNome: 'TelefoneCelular' },
    { field: 'emailResidencial', campoNome: 'EmailResidencial' }
  ],
  enderecos: [
    { cepField: 'cepResidencial', campoNomeCep: 'CEPResidencial', campoNomeEstado: 'EstadoResidencial', campoNomeMunicipio: 'MunicipioResidencial' }
  ]
};

export const DADOS_REQUERIMENTO_ENDERECO_PROFISSIONAL = {
  campos: [
    { field: 'logradouroProfissional', campoNome: 'LogradouroComercial' },
    { field: 'numeroProfissional', campoNome: 'NumeroComercial' },
    { field: 'complementoProfissional', campoNome: 'ComplementoComercial' },
    { field: 'bairroProfissional', campoNome: 'BairroComercial' },
    { field: 'telefoneProfissional', campoNome: 'TelefoneComercial' },
    { field: 'celularProfissional', campoNome: 'Telefone2Comercial' },
    { field: 'emailProfissional', campoNome: 'EmailComercial' }
  ],
  enderecos: [
    { cepField: 'cepProfissional', campoNomeCep: 'CEPComercial', campoNomeEstado: 'EstadoComercial', campoNomeMunicipio: 'MunicipioComercial' }
  ]
};
